import { BaseSyntheticEvent, Dispatch, ReactNode, SetStateAction } from "react";

import styles from "./BoardListView.module.css";

import Search, { SearchFilters, SearchOptions } from "./Search";

interface BoardListViewProps {
    data: any;
    renderItem: (...args: any) => ReactNode;

    header?: JSX.Element;
    widthRatio?: number[];

    itemsInRow?: number;
    rows?: number;

    pagenation?: boolean;
    currentPageNo?: number;
    lastPageNo?: number;
    onPageChanged?: (current: number) => void;

    searchFilters?: SearchFilters[];
    searchButtonBackground?: string;
    setSearchOptions?: Dispatch<SetStateAction<SearchOptions | undefined>>;

    background?: string;
    color?: string;
    margin?: string;
    padding?: string;
}

/**
 * @author khy
 * @date 2022-08-24 14:16
 * @remark 리스트뷰
 * @param data (required)
 * 테이블에 사용할 데이터
 * @param renderItem (required)
 * 각 item 을 렌더링할 때 데이터 렌더링 함수
 * @param header (optional)
 * header 부분의 JSX DOM
 * @param widthRatio (optional)
 * 하나의 행에 여러개의 item 이 들어갈때 각각 item 이 차지하는 넓이의 비율
 * @param itemsInRow (optional)
 * 하나의 행에 표현할 item 갯수
 * @param rows (optional)
 * 표현할 최대 행의 갯수
 * rows > data.length 일때, 비어있는 항목을 렌더링함
 * rows < data.length 일때, rows 의 갯수까지만 표현
 * @param pagenation (optional)
 * 페이지네이션 표현 여부
 * @param currentPageNo (optional)
 * 현재 페이지 넘버
 * @param lastPageNo (optional)
 * 페이지네이션 페이지 갯수 (pagenation true 인 경우 필수)
 * @param onPageChanged (optional)
 * 페이지네이션 버튼 클릭 시 실행될 callback 함수 (pagenation true 인 경우 필수)
 * @param background (optional)
 * ListView Component Style background
 * @param color (optional)
 * ListView Component Style color
 * @param margin(optional)
 * ListView Component Style margin
 * @param padding(optional)
 * ListView Component Style padding
 * @returns ListView Component JSX
 */

function BoardListView(props: BoardListViewProps) {
    const itemsInRow = props.itemsInRow ? props.itemsInRow : 1;
    /**
     * renderItem 들의 Col width 을 설정해주기 위한 함수
     * @param widthRatios ex) [ 1.5, 4, 1, 3, 0.5 ] col width
     * @return ex) [ 15, 40, 10, 30, 5 ] // widthRatios 를 총합 100% 이내로 각각 비율에 맞게 나눠 줌
     */

    const resizeColumnWidth = (widthRatio: number[] | undefined) => {
        const ratio = !widthRatio
            ? itemsInRow
                ? Array.from({ length: itemsInRow }, () => 1)
                : Array.from({ length: props.data.length }, () => 1)
            : widthRatio;

        const divide = ratio.reduce((prev, current) => prev + current, 0);
        const per = 100 / divide;
        const widths = ratio.map((e) => (e * per).toFixed(2));

        return widths;
    };

    const widths = resizeColumnWidth(props.widthRatio);

    /**
     * 페이지 버튼 클릭 이벤트
     * @param event
     */

    const handlePageClick = (event: BaseSyntheticEvent) => {
        const value = event.currentTarget.getAttribute("data-dest");
        const number = parseInt(value);
        if (props.onPageChanged !== undefined) {
            props.onPageChanged(number);
        }
    };

    /**
     * 페이지 버튼 DOM 생성 함수
     * @param current 현재 페이지 번호
     * @param end 마지막 페이지 번호
     *
     * @return 페이지네이션 JSX.Element[]
     */
    const generatePages = (current: number, end: number) => {
        const DOM: JSX.Element[] = [];
        const pageStart = current - 2 > 0 ? current - 2 : 1;

        // DOM.push(<div className={'pageButton shortcut'} key={'page_first'} style={{ backgroundImage: 'url(images/paging/first.png)' }} onClick={handlePageClick} data-dest={1}></div>)

        if (pageStart !== 1 && end > 5) {
            DOM.push(
                <div
                    className={`${styles.pageButton} ${styles.shortcut}`}
                    key={"page_prev"}
                    style={{ backgroundImage: "url(../../img/prev.png)" }}
                    onClick={handlePageClick}
                    data-dest={current - 5 >= 1 ? current - 5 : 1}
                ></div>
            );
        }

        for (let i = end > 5 && pageStart >= end - 4 ? end - 4 : end > 5 ? pageStart : 1; i <= pageStart + 4 && i <= end; i++) {
            DOM.push(
                <div
                    className={i === current ? `${styles.pageButton} ${styles.active}` : styles.pageButton}
                    key={`page_${i}`}
                    onClick={handlePageClick}
                    data-dest={i}
                >
                    {i}
                </div>
            );
        }

        if (pageStart + 5 <= end) {
            DOM.push(
                <div
                    className={`${styles.pageButton} ${styles.shortcut}`}
                    key={"page_next"}
                    style={{ backgroundImage: "url(../../img/next.png)" }}
                    onClick={handlePageClick}
                    data-dest={current + 5 <= end ? current + 5 : end}
                ></div>
            );
        }

        // DOM.push(<div className={'pageButton shortcut'} key={'page_last'} style={{ backgroundImage: 'url(images/paging/last.png)' }} onClick={handlePageClick} data-dest={pageCount}></div>)

        return DOM;
    };

    const isArrayData = Array.isArray(props.data);

    const rows = props.rows ? props.rows : isArrayData ? Math.ceil(props.data.length / itemsInRow!) : 1;

    return (
        <>
            <div className={isArrayData ? `${styles.table}` : `${styles.full_table}`} style={{ margin: props.margin, padding: props.padding }}>
                {props.header}
                {/* <div className={styles.rowGroup}> */}
                {isArrayData
                    ? Array.from(Array(rows)).map((empty: any, rowIndex: number) => {
                          return (
                              <div className={styles.rowGroup} key={`row[${rowIndex}]`}>
                                  {Array.from(Array(itemsInRow)).map((empty: any, colIndex) => {
                                      const width = widths?.[colIndex] ? `${widths[colIndex]}%` : undefined;
                                      const data = props.data?.[rowIndex * itemsInRow! + colIndex] ?? null;

                                      return (
                                          <div key={`row[${rowIndex}][${colIndex}]`} className={itemsInRow !== 1 ? styles.center : ""} style={{ width }}>
                                              {props.renderItem(data)}
                                          </div>
                                      );
                                  })}
                              </div>
                          );
                      })
                    : props.renderItem(props.data)}
                {/* </div> */}
            </div>
            {(props.pagenation === true || props.searchFilters) && (
                <div className={styles.grid}>
                    {props.pagenation === true && props.currentPageNo !== undefined && props.lastPageNo !== undefined && props.onPageChanged !== undefined && (
                        <div className={styles.pageList}>{generatePages(props.currentPageNo, props.lastPageNo)}</div>
                    )}
                    {props.searchFilters && props.setSearchOptions && <Search filters={props.searchFilters} doSearch={props.setSearchOptions} />}
                </div>
            )}
        </>
    );
}

export default BoardListView;
